'use client';

import { useSearchParams } from 'next/navigation';

import { ACCOUNT_COPY } from '@eliadmin/core/src/components/dlz/account/account-copy';
import type { IconName } from '@eliadmin/core/src/components/dlz/ui/icon';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocalePathname } from '@eliadmin/core/src/hooks/use-locale-pathname';
import { arePathsEqual } from '@eliadmin/core/src/utils/url-utils';

export type AccountPageKey = keyof typeof ACCOUNT_COPY.pages;

export type AccountPage = {
  key: AccountPageKey;
  label: string;
  icon: IconName;
  href: () => string;
};

/** The account navigation of Figma „Account nav“ (60317:24489), in its order. */
export const ACCOUNT_PAGES: AccountPage[] = [
  {
    key: 'profile',
    label: ACCOUNT_COPY.pages.profile.label,
    icon: 'user',
    href: () => ROUTES.PROFILE(),
  },
  {
    key: 'password',
    label: ACCOUNT_COPY.pages.password.label,
    icon: 'lock-key',
    href: () => ROUTES.CHANGE_PASSWORD(),
  },
  {
    key: 'orders',
    label: ACCOUNT_COPY.pages.orders.label,
    icon: 'package',
    href: () => ROUTES.ORDERS(),
  },
  {
    key: 'favorites',
    label: ACCOUNT_COPY.pages.favorites.label,
    icon: 'heart',
    href: () => ROUTES.FAVORITES(),
  },
];

/** The order detail lives on the orders URL: `/objednavky?objednavka=2026104512`. */
export const ORDER_PARAM = 'objednavka';

export function orderDetailHref(identifier: string) {
  return `${ROUTES.ORDERS()}?${ORDER_PARAM}=${encodeURIComponent(identifier)}`;
}

/** The account page of the current URL (null on other profile URLs of the core). */
export function useAccountPage() {
  const pathname = useLocalePathname();
  return ACCOUNT_PAGES.find(page => arePathsEqual(page.href(), pathname)) ?? null;
}

/** Order number of the detail view, null on the list. Needs a `<Suspense>` above it. */
export function useOrderParam() {
  return useSearchParams().get(ORDER_PARAM);
}
